/**
 * The sidebar's Today block (TMMAPP-0100, prototype P:143–160): the viewer's next meeting, set as a
 * block under the nav (a top `line` rule, not a card). The label is "TODAY" when the meeting starts
 * today in the viewer's zone (or is live), otherwise "NEXT" (D1 keeps the 7-day look-ahead). A
 * full-width JOIN when the calendar entry carries a `meetingUrl`, else a full-width "OPEN SESSION";
 * the "Calendar" link (D1) sits under it.
 */
import { useState } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { MeetingCalendarEntry } from '@/api/meetings';
import { useMeetingWriteVersion } from '@/features/meetings/meetingWrites';
import { meetingSessionPath, useNextMeeting } from '@/features/meetings/nextMeeting';
import { addCivilDays } from '@/shared/servedWindow';
import { formatInstant, formatList, formatZoneAbbreviation } from '@/i18n/format';
import { personName } from '@/i18n/personName';
import { useLocale } from '@/i18n/useLocale';
import { todayCivilInZone, utcToZonedCivil } from '@/i18n/zonedCivil';
import { useMe } from '@/session/meHooks';
import { resolveTokens } from '@/theme/theme';
import { sidebarCanvas } from './sidebarPaper';

const LABEL_ID = 'sidebar-today-label';

function durationMinutes(entry: MeetingCalendarEntry): number | null {
  const minutes = Math.round((Date.parse(entry.endsAt) - Date.parse(entry.startsAt)) / 60_000);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : null;
}

export function TodayBlock() {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const me = useMe();
  const { pathname } = useLocation();
  const zone = me.user.timezone ?? 'UTC';
  const today = todayCivilInZone(zone);
  // A move to another meetings page reads again (a meeting may have been scheduled or started).
  const [meetingsPath, setMeetingsPath] = useState<string | null>(null);
  if (pathname.includes('/meetings') && pathname !== meetingsPath) setMeetingsPath(pathname);
  // So does a Start or Finish on a meeting's session (TMMAPP-0155 item 11).
  const writes = useMeetingWriteVersion();
  // The one next-meeting rule (TMMAPP-0152 §B 12), over the 7-day look-ahead (D1).
  const { entry } = useNextMeeting({
    window: { from: today, to: addCivilDays(today, 7) },
    reloadKey: `${meetingsPath ?? ''}#${writes}`,
  });

  if (entry === undefined || entry === null) return null;
  // A standalone meeting's session is under My Money Meetings (TMMAPP-0159 §D 22).
  const sessionTo = meetingSessionPath(entry);
  const virtualAccountName =
    me.virtualAccounts.find((d) => d.id === entry.virtualAccountId)?.name ?? '';
  const startsAt = entry.startsAt;
  const isToday = entry.live || utcToZonedCivil(startsAt, zone).date === todayCivilInZone(zone);
  const minutes = durationMinutes(entry);
  const when = t(minutes === null ? 'shell.meetingWhenNoEnd' : 'shell.meetingWhen', {
    date: formatInstant(startsAt, locale, zone, { month: 'short', day: 'numeric' }),
    time: formatInstant(startsAt, locale, zone, { timeStyle: 'short' }),
    zone: formatZoneAbbreviation(startsAt, locale, zone),
    minutes,
  });
  const names = [
    ...(entry.organizerDisplayName === null ? [] : [entry.organizerDisplayName]),
    ...entry.invitees.map((person) => person.displayName),
  ].map((name) => personName(name, t));
  const who = names.length === 0 ? '' : formatList(names, locale);
  const where =
    virtualAccountName.length > 0 && who.length > 0
      ? t('shell.meetingWhere', { virtualAccount: virtualAccountName, who })
      : virtualAccountName || who;
  const meta = { mt: '3px', color: tokens.text2 } as const;

  return (
    <Box
      role="group"
      aria-labelledby={LABEL_ID}
      sx={{
        m: '14px 12px 0',
        p: '14px 12px 4px',
        borderTop: '1px solid',
        borderColor: tokens.line,
      }}
    >
      <Typography
        id={LABEL_ID}
        variant="overline"
        component="div"
        sx={{ letterSpacing: '0.09em', color: tokens.textLabel(sidebarCanvas(tokens)) }}
      >
        {t(isToday ? 'meetings.today' : 'shell.nextMeeting')}
      </Typography>
      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap' }}>
          <Typography variant="subtitle2">{entry.title}</Typography>
          {entry.live && (
            // The live pill is the static `brand` / `brandInk` (visual contract §3): 10/500
            // uppercase .06em, radius 11, padding 2 7.
            <Typography
              variant="micro"
              data-live=""
              sx={{
                borderRadius: '11px',
                px: '7px',
                py: '2px',
                bgcolor: tokens.brand,
                color: tokens.brandInk,
              }}
            >
              {t('shell.inProgress')}
            </Typography>
          )}
        </Box>
        {when.length > 0 && (
          <Typography variant="caption" component="div" sx={meta}>
            {when}
          </Typography>
        )}
        {where.length > 0 && (
          <Typography variant="caption" component="div" sx={meta}>
            {where}
          </Typography>
        )}
        {entry.meetingUrl !== null && entry.meetingUrl.length > 0 ? (
          <Button
            fullWidth
            size="small"
            variant="contained"
            href={entry.meetingUrl}
            target="_blank"
            rel="noreferrer"
            sx={{ mt: '9px' }}
          >
            {t('meetings.join')}
          </Button>
        ) : (
          <Button
            fullWidth
            size="small"
            variant="outlined"
            component={RouterLink}
            to={sessionTo}
            sx={{ mt: '9px' }}
          >
            {t('meetings.openSession')}
          </Button>
        )}
        <Button
          size="small"
          component={RouterLink}
          to="/me/meetings"
          sx={{ mt: '6px', px: 0, minWidth: 0, fontSize: 12, textTransform: 'none' }}
        >
          {t('meetings.calendar')}
        </Button>
      </Box>
    </Box>
  );
}
